import { Banner } from '@evisort/eds/components/Banner/Banner';
import { Button } from '@evisort/eds/components/Button/Button';
import { FormField } from '@evisort/eds/components/FormField/FormField';
import { Layout } from '@evisort/eds/components/Layout/Layout';
import { Link } from '@evisort/eds/components/Link/Link';
import { Text } from '@evisort/eds/components/Text/Text';
import { useForm, useStore } from '@tanstack/react-form';
import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { activateUserAccount } from '~/api/login';
import { BRAND_NAME } from '~/common/constants/brand';
import { AuthMarketingHeader } from '~/components/AuthPageLayout/AuthMarketingHeader';
import { AuthPageLayout } from '~/components/AuthPageLayout/AuthPageLayout';
import { completePasswordResetHandoff } from '~/components/LoginPage/loginHandoff';
import {
  isPasswordFullyValid,
  PasswordRequirementsList,
} from '~/components/PasswordRequirementsList/PasswordRequirementsList';
import { QueryParamType } from '~/enums/QueryParamType';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useFormFocusManager } from '~/hooks/useFormFocusManager';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { getPageSearchQueryByKey } from '~/utils/searchQuery';

import styles from './ActivationPage.module.css';

const messages = defineMessages({
  cardSubtitle: {
    id: 'ActivationPage.cardSubtitle',
    defaultMessage: 'Fill in the details below to activate your invitation.',
    description: 'Subtitle for the activation form card',
  },
  cardTitle: {
    id: 'ActivationPage.cardTitle',
    defaultMessage: 'Create your account',
    description: 'Title for the activation form card',
  },
  confirmPasswordLabel: {
    id: 'ActivationPage.confirmPasswordLabel',
    defaultMessage: 'Confirm Password',
    description: 'Label for the confirm password input',
  },
  firstNameLabel: {
    id: 'ActivationPage.firstNameLabel',
    defaultMessage: 'First Name',
    description: 'Label for the first name input',
  },
  genericErrorMessage: {
    id: 'ActivationPage.genericErrorMessage',
    defaultMessage: 'An error occurred. Try again.',
    description: 'Generic error message when activation fails',
  },
  invalidLinkMessage: {
    id: 'ActivationPage.invalidLinkMessage',
    defaultMessage:
      'This activation link is no longer valid. Contact your administrator for a new invitation.',
    description: 'Message shown when the activation link has no token',
  },
  invalidLinkTitle: {
    id: 'ActivationPage.invalidLinkTitle',
    defaultMessage: 'Invalid Activation Link',
    description: 'Title shown when the activation link has no token',
  },
  jobTitleLabel: {
    id: 'ActivationPage.jobTitleLabel',
    defaultMessage: 'Job Title',
    description: 'Label for the job title input',
  },
  lastNameLabel: {
    id: 'ActivationPage.lastNameLabel',
    defaultMessage: 'Last Name',
    description: 'Label for the last name input',
  },
  passwordLabel: {
    id: 'ActivationPage.passwordLabel',
    defaultMessage: 'Password',
    description: 'Label for the password input',
  },
  passwordRequirementsError: {
    id: 'ActivationPage.passwordRequirementsError',
    defaultMessage: "Your password doesn't meet the requirements below.",
    description: 'Error shown when the password fails validation',
  },
  passwordsDontMatchError: {
    id: 'ActivationPage.passwordsDontMatchError',
    defaultMessage: "Passwords don't match.",
    description: 'Error shown when the confirm password does not match',
  },
  submitButton: {
    id: 'ActivationPage.submitButton',
    defaultMessage: 'Start Using {brandName}',
    description: 'Button to submit the activation form',
  },
  subtitle: {
    id: 'ActivationPage.subtitle',
    defaultMessage:
      "You've been invited to join your team's contract intelligence platform. Create your account to get started.",
    description: 'Subtitle for the activation page marketing panel',
  },
  successMessage: {
    id: 'ActivationPage.successMessage',
    defaultMessage:
      "Your password has been updated. You'll be redirected to the platform shortly.",
    description: 'Message shown after a successful account activation',
  },
  successTitle: {
    id: 'ActivationPage.successTitle',
    defaultMessage: 'Account Activated',
    description: 'Title shown after a successful account activation',
  },
  termsOfServiceLeadIn: {
    id: 'ActivationPage.termsOfServiceLeadIn',
    defaultMessage: 'By continuing, you agree to our',
    description: 'Lead-in text before the terms of service link',
  },
  termsOfServiceLinkText: {
    id: 'ActivationPage.termsOfServiceLinkText',
    defaultMessage: 'Terms of Service',
    description: 'Link text for the terms of service',
  },
  title: {
    id: 'ActivationPage.title',
    defaultMessage: 'Welcome',
    description: 'Headline on the activation page marketing panel',
  },
});

interface ActivationFormValues {
  firstName: string;
  lastName: string;
  jobTitle: string;
  password: string;
  /** Spelled to match the id the legacy page rendered (`confirmedPassword`),
   * which is the stable hook any external automation would have keyed on. */
  confirmedPassword: string;
}

interface ActivationResponse {
  msg?: string;
  token?: string;
  first_login?: boolean;
  user_id?: string;
  client_id?: string;
}

interface ActivationApiError {
  response?: {
    data?: {
      errors?: string[];
      msg?: string;
    };
  };
}

type ActivationView = 'form' | 'no-token' | 'success';

function Page() {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const featureFlags = {
    searchAsHome: useFlag(FlagType.SearchAsHomePage),
  };

  const token = getPageSearchQueryByKey<string | null>(QueryParamType.Token);
  const slug = getPageSearchQueryByKey<string | null>(QueryParamType.Slug);
  const tokenType = getPageSearchQueryByKey<string | null>(
    QueryParamType.StytchTokenType,
  );

  const [view, setView] = useState<ActivationView>('form');
  const [apiError, setApiError] = useState<string | null>(null);

  const form = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      jobTitle: '',
      password: '',
      confirmedPassword: '',
    } as ActivationFormValues,
    onSubmit: async ({ value }) => {
      setApiError(null);

      if (!token) {
        setView('no-token');
        return;
      }

      try {
        const response = (await activateUserAccount({
          token,
          slug,
          tokenType,
          formData: {
            first_name: value.firstName,
            last_name: value.lastName,
            job_title: value.jobTitle,
            new_password: value.password,
          },
        })) as ActivationResponse;

        await completePasswordResetHandoff(response, {
          featureFlags,
          event: 'User First Login',
          formatDate,
        });
        setView('success');
      } catch (error) {
        const data = (error as ActivationApiError)?.response?.data;
        const message =
          (Array.isArray(data?.errors) &&
            data.errors.length > 0 &&
            data.errors.join(' ')) ||
          data?.msg ||
          intl.formatMessage(messages.genericErrorMessage);
        setApiError(message);
      }
    },
  });

  useFormFocusManager(form);

  const submissionAttempts = useStore(
    form.store,
    (state) => state.submissionAttempts,
  );
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const password = useStore(form.store, (state) => state.values.password);

  return (
    <AuthPageLayout
      marketing={
        <AuthMarketingHeader
          subtitle={intl.formatMessage(messages.subtitle)}
          title={intl.formatMessage(messages.title)}
        />
      }
      marketingHeader={
        <AuthMarketingHeader
          subtitle={intl.formatMessage(messages.subtitle)}
          title={intl.formatMessage(messages.title)}
          variant="compact"
        />
      }
    >
      <Layout direction="column" spacing="l">
        <Layout direction="column" spacing="s">
          <Text as="h1" variant="heading-m">
            {intl.formatMessage(messages.cardTitle)}
          </Text>
          <Text as="p" tone="subdued" variant="body-m">
            {intl.formatMessage(messages.cardSubtitle)}
          </Text>
        </Layout>

        {view === 'no-token' && (
          <Banner
            status="critical"
            text={intl.formatMessage(messages.invalidLinkMessage)}
            title={intl.formatMessage(messages.invalidLinkTitle)}
          />
        )}

        {view === 'success' && (
          <Banner
            status="positive"
            text={intl.formatMessage(messages.successMessage)}
            title={intl.formatMessage(messages.successTitle)}
          />
        )}

        {view === 'form' && (
          <Layout
            action="#"
            as="form"
            className={styles.form}
            direction="column"
            method="POST"
            noValidate
            spacing="l"
            onSubmit={(event) => {
              event.preventDefault();
              form.handleSubmit().catch(() => {
                // Errors are surfaced via apiError state above.
              });
            }}
          >
            {apiError && (
              <Banner
                status="critical"
                text={apiError}
                onDismiss={() => setApiError(null)}
              />
            )}

            <Layout className={styles.fieldRow} spacing="m" wrap>
              <form.Field name="firstName">
                {(field) => (
                  <FormField
                    label={intl.formatMessage(messages.firstNameLabel)}
                    name={field.name}
                    type="text"
                    value={field.state.value}
                    onChange={(next) => field.handleChange(next ?? '')}
                  />
                )}
              </form.Field>

              <form.Field name="lastName">
                {(field) => (
                  <FormField
                    label={intl.formatMessage(messages.lastNameLabel)}
                    name={field.name}
                    type="text"
                    value={field.state.value}
                    onChange={(next) => field.handleChange(next ?? '')}
                  />
                )}
              </form.Field>
            </Layout>

            <form.Field name="jobTitle">
              {(field) => (
                <FormField
                  label={intl.formatMessage(messages.jobTitleLabel)}
                  name={field.name}
                  type="text"
                  value={field.state.value}
                  onChange={(next) => field.handleChange(next ?? '')}
                />
              )}
            </form.Field>

            <Layout className={styles.fieldRow} spacing="m" wrap>
              <form.Field
                name="password"
                validators={{
                  onChange: ({ value }) =>
                    isPasswordFullyValid(value)
                      ? undefined
                      : intl.formatMessage(messages.passwordRequirementsError),
                  onSubmit: ({ value }) =>
                    isPasswordFullyValid(value)
                      ? undefined
                      : intl.formatMessage(messages.passwordRequirementsError),
                }}
              >
                {(field) => (
                  <FormField
                    hint={
                      submissionAttempts > 0
                        ? (field.state.meta.errors[0] ?? undefined)
                        : undefined
                    }
                    label={intl.formatMessage(messages.passwordLabel)}
                    name={field.name}
                    status={
                      submissionAttempts > 0 && field.state.meta.errors[0]
                        ? 'critical'
                        : undefined
                    }
                    type="password"
                    value={field.state.value}
                    onChange={(next) => field.handleChange(next ?? '')}
                  />
                )}
              </form.Field>

              <form.Field
                name="confirmedPassword"
                validators={{
                  onChangeListenTo: ['password'],
                  onChange: ({ fieldApi, value }) =>
                    value === fieldApi.form.getFieldValue('password')
                      ? undefined
                      : intl.formatMessage(messages.passwordsDontMatchError),
                  onSubmit: ({ fieldApi, value }) =>
                    value === fieldApi.form.getFieldValue('password')
                      ? undefined
                      : intl.formatMessage(messages.passwordsDontMatchError),
                }}
              >
                {(field) => (
                  <FormField
                    hint={
                      submissionAttempts > 0
                        ? (field.state.meta.errors[0] ?? undefined)
                        : undefined
                    }
                    label={intl.formatMessage(messages.confirmPasswordLabel)}
                    name={field.name}
                    status={
                      submissionAttempts > 0 && field.state.meta.errors[0]
                        ? 'critical'
                        : undefined
                    }
                    type="password"
                    value={field.state.value}
                    onChange={(next) => field.handleChange(next ?? '')}
                  />
                )}
              </form.Field>
            </Layout>

            <PasswordRequirementsList password={password} />

            <Layout align="center" spacing="xs" wrap>
              <Text as="p" tone="subdued" variant="body-s">
                {intl.formatMessage(messages.termsOfServiceLeadIn)}
              </Text>
              <Link
                external
                href="https://www.evisort.com/terms/"
                text={intl.formatMessage(messages.termsOfServiceLinkText)}
              />
            </Layout>

            <Layout className={styles.submitAction}>
              <Button
                isLoading={isSubmitting}
                label={intl.formatMessage(messages.submitButton, {
                  brandName: BRAND_NAME,
                })}
                text={intl.formatMessage(messages.submitButton, {
                  brandName: BRAND_NAME,
                })}
                type="submit"
                variant="primary"
              />
            </Layout>
          </Layout>
        )}
      </Layout>
    </AuthPageLayout>
  );
}

export default Page;
